import React from 'react';
import { CreditCard, KeyRound, Wallet, Building2, Lock, Banknote } from 'lucide-react';
import { theme } from '../styles/GlobalStyle';

interface LoadingPlaceholderProps {
  title: string;
  description?: string;
}

export const LoadingPlaceholder: React.FC<LoadingPlaceholderProps> = ({ title, description }) => {
  return (
    <div className="flex items-center justify-center">
      <div className="w-full relative flex flex-col items-center justify-center">
        {/* Pulsing background circle */}
        <div className="absolute h-32 w-32 animate-ping rounded-full bg-blue-300/90 z-[-1]" />
        <div className="absolute h-32 w-32 rounded-full bg-blue-300/70 z-[-1]" />

        {/* Content container */}
        <div className="min-w-1/3 relative flex flex-col items-center justify-center space-y-4 rounded-lg bg-white/90 p-24 z-10">
          {/* Rotating icon carousel */}
          <div className="relative h-16 w-16 flex items-center justify-center">
            <CreditCard
              className="absolute h-8 w-8 opacity-0 animate-[fadeInOut_18s_linear_infinite]"
              style={{ animationDelay: '0s', color: theme.blue4 }}
            />
            <KeyRound
              className="absolute h-8 w-8 opacity-0 animate-[fadeInOut_18s_linear_infinite]"
              style={{ animationDelay: '3s', color: theme.blue4 }}
            />
            <Lock
              className="absolute h-8 w-8 opacity-0 animate-[fadeInOut_18s_linear_infinite]"
              style={{ animationDelay: '6s', color: theme.blue4 }}
            />
            <Wallet
              className="absolute h-8 w-8 opacity-0 animate-[fadeInOut_18s_linear_infinite]"
              style={{ animationDelay: '9s', color: theme.blue4 }}
            />
            <Banknote
              className="absolute h-8 w-8 opacity-0 animate-[fadeInOut_18s_linear_infinite]"
              style={{ animationDelay: '12s', color: theme.blue4 }}
            />
            <Building2
              className="absolute h-8 w-8 opacity-0 animate-[fadeInOut_18s_linear_infinite]"
              style={{ animationDelay: '15s', color: theme.blue4 }}
            />
          </div>

          <div className="space-y-2 text-center">
            <p className="text-lg font-medium text-gray-800" style={{ fontFamily: 'Majorant-Regular' }}>
              {title}
            </p>
            {description && (
              <p className="text-sm text-gray-600" style={{ fontFamily: 'Majorant-Regular' }}>
                {description}
              </p>
            )}
          </div>

          {/* Progress dots */}
          <div className="flex space-x-2">
            {[1, 2, 3].map((dot) => (
              <div
                key={dot}
                className="h-2 w-2 rounded-full bg-gray-300"
                style={{
                  animation: 'pulse 1.5s ease-in-out infinite',
                  animationDelay: `${(dot - 1) * 0.5}s`,
                  backgroundColor: theme.blue4,
                }}
              />
            ))}
          </div>
        </div>
      </div>
      <style>
        {`
          @keyframes fadeInOut {
            0%, 16.67%, 100% {
              opacity: 0;
              transform: translateY(10px);
            }
            3.33%, 13.33% {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes scaleIn {
            0% {
              transform: scale(0);
            }
            100% {
              transform: scale(1);
            }
          }

          @keyframes checkmark {
            0% {
              opacity: 0;
              transform: rotate(45deg) scale(0);
            }
            100% {
              opacity: 1;
              transform: rotate(45deg) scale(1);
            }
          }
        `}
      </style>
    </div>
  );
};
